/**
 * ViewportManager — camada ÚNICA de responsividade. Observa o viewport REALMENTE
 * disponível (visualViewport quando existe), DPR, safe-areas e orientação; classifica
 * em size classes; calcula uma escala de UI; e publica tudo como CSS variables no :root
 * (--ui-scale, --touch-size, --hud-gap, --safe-*) + atributos data-size-class/data-orientation.
 * Nenhum outro componente instala listener de resize — todos assinam onChange().
 * Pensa em ESPAÇO DISPONÍVEL, nunca em modelo de aparelho.
 */
export type SizeClass = "compact" | "standard" | "large" | "tablet";
export type Orientation = "portrait" | "landscape";

export interface SafeInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface ViewportState {
  width: number;
  height: number;
  aspect: number;
  dpr: number;
  orientation: Orientation;
  sizeClass: SizeClass;
  uiScale: number;
  safe: SafeInsets;
}

class ViewportManager {
  state!: ViewportState;
  private readonly listeners = new Set<(s: ViewportState) => void>();
  private userScale = 1; // acessibilidade: Small/Default/Large (multiplica a escala automática)
  private readonly probe: HTMLDivElement;
  private pending = false;

  constructor() {
    // probe para resolver env(safe-area-inset-*) em px reais
    this.probe = document.createElement("div");
    this.probe.style.cssText =
      "position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;" +
      "padding-top:env(safe-area-inset-top);padding-right:env(safe-area-inset-right);" +
      "padding-bottom:env(safe-area-inset-bottom);padding-left:env(safe-area-inset-left);";
    document.body.appendChild(this.probe);

    this.compute();
    const onResize = (): void => this.schedule();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", onResize);
      window.visualViewport.addEventListener("scroll", onResize);
    }
  }

  /**
   * Debounce por microtask (não por rAF: rAF é congelado com a aba oculta e poderia
   * travar o recálculo). Coalesce múltiplos eventos de resize do mesmo tick num único compute.
   */
  private schedule(): void {
    if (this.pending) return;
    this.pending = true;
    Promise.resolve().then(() => {
      this.pending = false;
      this.compute();
    });
  }

  onChange(fn: (s: ViewportState) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Escala de acessibilidade definida em Settings (multiplica a escala automática). */
  setUserScale(mult: number): void {
    this.userScale = mult;
    this.compute();
  }

  private readSafe(): SafeInsets {
    const cs = getComputedStyle(this.probe);
    const px = (v: string): number => Math.max(0, Math.round(parseFloat(v) || 0));
    return { top: px(cs.paddingTop), right: px(cs.paddingRight), bottom: px(cs.paddingBottom), left: px(cs.paddingLeft) };
  }

  private classify(w: number): SizeClass {
    if (w < 340) return "compact";
    if (w < 400) return "standard";
    if (w < 600) return "large";
    return "tablet";
  }

  private compute(): void {
    const vv = window.visualViewport;
    const width = Math.round(vv?.width ?? window.innerWidth);
    const height = Math.round(vv?.height ?? window.innerHeight);
    const aspect = width / Math.max(1, height);
    const dpr = window.devicePixelRatio || 1;
    const orientation: Orientation = width >= height ? "landscape" : "portrait";
    const sizeClass = this.classify(width);
    const safe = this.readSafe();

    // escala automática relativa a uma referência (390x780), limitada, × escala do usuário
    const auto = Math.min(width / 390, height / 780);
    const uiScale = Math.min(1.5, Math.max(0.8, auto * this.userScale));

    this.state = { width, height, aspect, dpr, orientation, sizeClass, uiScale, safe };
    this.applyCssVars();
    for (const fn of this.listeners) fn(this.state);
  }

  private applyCssVars(): void {
    const r = document.documentElement.style;
    const s = this.state;
    r.setProperty("--viewport-width", `${s.width}px`);
    r.setProperty("--viewport-height", `${s.height}px`);
    r.setProperty("--viewport-left", `${window.visualViewport?.offsetLeft ?? 0}px`);
    r.setProperty("--viewport-top", `${window.visualViewport?.offsetTop ?? 0}px`);
    r.setProperty("--ui-scale", s.uiScale.toFixed(3));
    r.setProperty("--touch-size", `${Math.round(Math.min(76, Math.max(46, 56 * s.uiScale)))}px`);
    r.setProperty("--hud-gap", `${Math.round(Math.min(14, Math.max(6, 8 * s.uiScale)))}px`);
    r.setProperty("--safe-top", `${s.safe.top}px`);
    r.setProperty("--safe-right", `${s.safe.right}px`);
    r.setProperty("--safe-bottom", `${s.safe.bottom}px`);
    r.setProperty("--safe-left", `${s.safe.left}px`);
    document.documentElement.setAttribute("data-size-class", s.sizeClass);
    document.documentElement.setAttribute("data-orientation", s.orientation);
  }
}

export const viewport = new ViewportManager();
